// Search index for the CPLEX Sankey dashboard.
//
// Flattens MPP rows into a list of distinct entities (customers, AU#s, orders,
// SKUs, bulks, containers, raws, rm types, vendors, PO numbers, days). Each
// entity carries the row indices it touches so the filter layer can derive a
// row subset (and from there, a node-id highlight set) by AND/OR-combining
// the chips the user has clicked.
//
// All inputs are the raw master_production_plan rows already loaded by
// CplexSankeyFullPage — the index is rebuilt client-side whenever the active
// run / model / day slice changes.

import type { MPPRow } from './types';

export type EntityKind =
  | 'customer'
  | 'au'
  | 'order'
  | 'sku'
  | 'bulk'
  | 'container'
  | 'raw'
  | 'rm_type'
  | 'vendor'
  | 'po_number'
  | 'day'
  // Free-text "contains" filter — created when the user commits the raw
  // query as a chip rather than picking a specific entity. The chip's
  // rowIndices are the union of every entity whose searchKey contains the
  // query tokens.
  | 'query';

export interface SearchEntity {
  kind: EntityKind;
  // Stable identifier — also the de-dup key inside a kind. Lower-cased,
  // whitespace-collapsed canonical value.
  value: string;
  // Display label as it should appear in chips and dropdown items.
  label: string;
  /**
   * Punctuation-folded version of `value` used by the ranker. Hyphens,
   * slashes, parens, dots etc. are collapsed to spaces so a search for
   * "SafeCure" matches both "SafeCure-1000" and "SafeCure 1000" without
   * surprising the user with separator-sensitivity.
   */
  searchKey: string;
  // Row indices (into the input MPPRow[]) where this entity appears. Used by
  // the filter layer to compute the matching subset.
  rowIndices: number[];
}

const ORDER_AU_RE = /^(.*?)\s*\((AU\d+)\)\s*$/;

function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Fold a label for matching: lowercase, replace any run of non-word
 * characters (hyphens, slashes, parens, dots, commas, etc.) with a single
 * space, then collapse whitespace. Use this for BOTH the entity searchKey
 * and the typed-query tokenisation so separator style never affects matches.
 */
export function normSearchKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function addEntity(
  map: Map<string, SearchEntity>,
  kind: EntityKind,
  rawLabel: string | null | undefined,
  rowIdx: number,
): void {
  if (rawLabel == null) return;
  const label = String(rawLabel).trim();
  if (label === '' || label === 'Dropped / Unfilled') return;
  const value = norm(label);
  const key = `${kind}::${value}`;
  let e = map.get(key);
  if (!e) {
    e = { kind, value, label, searchKey: normSearchKey(label), rowIndices: [] };
    map.set(key, e);
  }
  e.rowIndices.push(rowIdx);
}

/**
 * Build the search index from the active slice's MPP rows.
 *
 * Returns one entity per (kind, normalised value) seen across all rows. The
 * `order` entity also implicitly contributes a `customer` and `au` entity so
 * users can search by either side of the "Customer Name (AU12345)" string.
 */
export function buildSearchIndex(rows: readonly MPPRow[]): SearchEntity[] {
  const map = new Map<string, SearchEntity>();
  rows.forEach((row, idx) => {
    if (row.order) {
      addEntity(map, 'order', row.order, idx);
      const m = row.order.match(ORDER_AU_RE);
      if (m) {
        const customer = m[1].trim();
        const au = m[2].trim();
        if (customer) addEntity(map, 'customer', customer, idx);
        if (au) addEntity(map, 'au', au, idx);
      } else {
        addEntity(map, 'customer', row.order, idx);
      }
    }
    addEntity(map, 'sku', row.sku, idx);
    addEntity(map, 'bulk', row.bulk, idx);
    addEntity(map, 'container', row.container, idx);
    addEntity(map, 'raw', row.raws, idx);
    addEntity(map, 'rm_type', row['rm type'], idx);
    addEntity(map, 'vendor', row.po_vendor, idx);
    addEntity(map, 'po_number', row.po_number, idx);
    if (row.day != null) addEntity(map, 'day', `Day ${row.day}`, idx);
  });
  return [...map.values()];
}

/**
 * Pretty label for a kind — used by the chip + dropdown UI to group results.
 */
export const KIND_LABELS: Record<EntityKind, string> = {
  customer: 'Customer',
  au: 'Order #',
  order: 'Order',
  sku: 'SKU',
  bulk: 'Bulk',
  container: 'Container',
  raw: 'Raw material',
  rm_type: 'RM type',
  vendor: 'Vendor',
  po_number: 'PO #',
  day: 'Day',
  query: 'Includes',
};

/**
 * Display order for the autocomplete groups — most-specific kinds first.
 */
export const KIND_ORDER: EntityKind[] = [
  'customer',
  'au',
  'order',
  'sku',
  'bulk',
  'container',
  'raw',
  'vendor',
  'po_number',
  'rm_type',
  'day',
];

// buildQueryChip lives in `./searchRank.ts` so it can share Fuse with the
// dropdown's ranker. Re-exported here for backwards compat.
export { buildQueryChip } from './searchRank';
